import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    Container,
    IconButton,
    Link,
    Typography,
    Paper,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    Snackbar,
    CircularProgress,
    Avatar,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// AssignmentIndOutlinedIcon, // Replaced by img tag for custom SVG
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MainDrawer from "../MainDrawer"; // Assuming this path is correct
import queryGraphql from "../../helpers/ApiInterface"; // Make sure this path is correct
import { useAuth } from "../../helpers/AuthProvider";

const openDrawerWidth = 240;
const collapsedDrawerWidth = 60;

const SonarQubeLogo = () => (
    <img
        src="/assets/sonarqube.svg" // Path from public folder
        alt="SonarQube Logo"
        style={{ width: '40px', height: '40px' }}
    />
);

// Date formatting utility
const formatDate = (timestamp: string | number | null | undefined): string => {
        if (timestamp === null || timestamp === undefined) return "N/A";
        let date: Date | null = null;
        try {
            if (typeof timestamp === 'number') {
                // Assume milliseconds if number. Add logic here if it might be seconds (e.g., multiply by 1000).
                date = new Date(timestamp);
            } else if (typeof timestamp === 'string') {
                // Try parsing as a number first (common for millisecond timestamps as strings)
                const nTs = Number(timestamp);
                // Check if it looks like a valid numeric timestamp string
                if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) {
                     date = new Date(nTs); // Use the parsed number
                } else {
                    // Otherwise, try parsing the string directly (e.g., ISO 8601 format)
                    date = new Date(timestamp);
                }
            }

            // Final validation check
            if (date && !isNaN(date.getTime())) {
                // Format as DD/MM/YYYY using 'en-GB' locale
                return date.toLocaleDateString('en-GB', {
                    day: '2-digit',   // Ensures DD
                    month: '2-digit',  // Ensures MM
                    year: 'numeric'   // Ensures YYYY
                });
            } else {
                console.error(`Invalid or unparseable date format received: ${timestamp}`);
                return "Invalid Date"; // Or return "N/A"
            }
        } catch (e) {
            console.error(`Error parsing date (${timestamp}):`, e);
            return "Error"; // Or return "N/A"
        }
    };


const SonarQubeIntegrationPageContent: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const {user} = useAuth();
    // States for fetched data
    const [fetchedUsername, setFetchedUsername] = useState<string | null>(null);
    const [fetchedPassword, setFetchedPassword] = useState<string | null>(null);
    const [fetchedInstalledOn, setFetchedInstalledOn] = useState<string | null>(null);
    const [fetchedDashboardUrl, setFetchedDashboardUrl] = useState<string | null>(null);

    const [isLoadingData, setIsLoadingData] = useState<boolean>(true); // Combined loading state
    const [pageLoadError, setPageLoadError] = useState<string | null>(null); // Combined error state

    // Static data that doesn't come from these specific APIs
    const staticSonarQubeUIData = {
        name: "SonarQube",
        category: "SECURITY",
        description: "An open platform for continuous inspection of code quality and security.",
    };

    const getSonarQubeCredentialsQueryString = `
            query Query {
        getSonarQubeCredentials
        }
    `;

    const getIntegrationsQueryString = `
        query GetIntegrations {
          getIntegrations {
            sonarQube {
              updatedAt
              repoUrl
            }
          }
        }
    `;

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoadingData(true);
            setPageLoadError(null);
            try {
                const [credsResponse, integrationsResponse] = await Promise.all([
                    queryGraphql(getSonarQubeCredentialsQueryString),
                    queryGraphql(getIntegrationsQueryString)
                ]);

                // Process credentials response
                if (credsResponse && credsResponse.getSonarQubeCredentials) {
                    setFetchedUsername(credsResponse.getSonarQubeCredentials.username);
                    setFetchedPassword(credsResponse.getSonarQubeCredentials.password);
                } else {
                    console.error("Credentials not found in API response:", credsResponse);
                    // Set specific error or throw to be caught below
                    throw new Error("Failed to fetch SonarQube credentials.");
                }

                // Process integrations response
                if (integrationsResponse && integrationsResponse.getIntegrations && integrationsResponse.getIntegrations.sonarQube) {
                    setFetchedInstalledOn(formatDate(integrationsResponse.getIntegrations.sonarQube.updatedAt));
                    setFetchedDashboardUrl('http://'+integrationsResponse.getIntegrations.sonarQube.repoUrl);
                } else {
                    console.error("SonarQube integration data not found in API response:", integrationsResponse);
                    // Set specific error or throw
                    throw new Error("Failed to fetch SonarQube integration details.");
                }

            } catch (error) {
                console.error("Error fetching SonarQube page data:", error);
                setPageLoadError(error instanceof Error ? error.message : "An unknown error occurred.");
                // Reset all fetched data on error
                setFetchedUsername(null);
                setFetchedPassword(null);
                setFetchedInstalledOn(null);
                setFetchedDashboardUrl(null);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array runs once on mount

    const handleNavigateToDashboard = () => {
        if (fetchedDashboardUrl) {
            window.open(fetchedDashboardUrl, "_blank", "noopener,noreferrer");
        } else {
            setSnackbarMessage("Dashboard URL is not available.");
            setSnackbarOpen(true);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const copyToClipboard = async (text: string, type: string) => {
        // ... (copyToClipboard function remains the same)
        if (!text) {
            setSnackbarMessage(`No ${type} to copy.`);
            setSnackbarOpen(true);
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage(`${type} copied to clipboard!`);
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage(`Failed to copy ${type}.`);
            setSnackbarOpen(true);
            console.error('Failed to copy: ', err);
        }
    };

    const CredentialDisplayBox: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
        // ... (CredentialDisplayBox component remains the same)
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>
                {label}
            </Typography>
            <Paper
                variant="outlined"
                sx={{
                    p: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f5f5f5',
                    borderColor: theme.palette.divider,
                    borderRadius: '4px',
                    minHeight: '30px',
                }}
            >
                <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', wordBreak: 'break-all', color: value ? 'text.primary' : 'text.disabled' }}>
                    {value || 'N/A'}
                </Typography>
                {value && (
                    <IconButton onClick={() => copyToClipboard(value, label)} size="small" aria-label={`Copy ${label}`}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                )}
            </Paper>
        </Box>
    );

    return (
        <>
            <Container
                disableGutters
                maxWidth={false}
                sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
            >
                {/* --- Header --- */}
                <Box sx={{ mb: 3 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                        <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer', fontFamily: 'Gilroy-Regular' }}>Integrations</Link>
                        <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}>{staticSonarQubeUIData.name}</Typography>
                    </Breadcrumbs>
                    <Box display="flex" justifyContent="space-between">
                    <Box display="flex" alignItems="center" flexDirection="row" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{staticSonarQubeUIData.name}</Typography>
                    </Box>
                    <IconButton
                                                                                                                                              aria-label="Help" 
                                                                                                                                              onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                                                                                              sx={{
                                                                                                                                                padding: 0, 
                                                                                                                                                border: 'none',  
                                                                                                                                                ml:2,
                                                                                                                                                '&:hover': {
                                                                                                                                                  
                                                                                                                                                  backgroundColor: 'action.hover', 
                                                                                                                                                },
                                                                                                                                                
                                                                                                                                              }}
                                                                                                                                            >
                                                                                                                                              <Box
                                                                                                                                                component="img"
                                                                                                                                                src="/assets/Help.svg" 
                                                                                                                                                alt="" 
                                                                                                                                                sx={{
                                                                                                                                                  width: 40,  
                                                                                                                                                  height: 40,
                                                                                                                                                  display: 'block', 
                                                                                                                                                }}
                                                                                                                                              />
                                                                                                                                            </IconButton>
                    </Box>
                </Box>

                {/* --- Main Content Card --- */}
                <Paper
                    elevation={1}
                    sx={{ maxWidth: '70vw', mx: 'auto', p: { xs: 2, md: 4 }, borderRadius: "12px", border: "1px solid #E0E0E0", display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    {/* Top Section */}
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <SonarQubeLogo />
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">{staticSonarQubeUIData.name}</Typography>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="0.75rem" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}>{staticSonarQubeUIData.category}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5 }}>{staticSonarQubeUIData.description}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' , display:'flex', flexDirection:'row'}}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Medium', fontSize:'14px' }}>
                                Installed On: {fetchedInstalledOn || "N/A"}
                            </Typography>
                            
                        </Box>
                    </Box>

                    {/* Middle Section */}
                    <Box sx={{ textAlign: 'center', my: { xs: 2, md: 3 }, width: '100%' }}>
                        <img
                            src="/assets/grafana_creds_screen.svg" // Path from public folder
                            alt="SonarQube Credentials Illustration"
                            style={{
                                width: 'auto',
                                height: '18vh', // Adjusted height for better visual match
                                marginBottom: theme.spacing(2)
                            }}
                        />
                        <Typography fontFamily="Gilroy-Bold" fontSize={{ xs: "20px", md: "24px" }} component="h2" sx={{ mb: 1 }}>You're Almost There</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', maxWidth: '500px', mx: 'auto', mb: 3 }}>
                            Click below button to go to SonarQube dashboard. Make sure you get the login credentials before you proceed.
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{ py: 1,px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '80px', bgcolor: "#F8F8F8", textAlign: 'left', maxWidth: '450px', mx: 'auto', mb: 4 }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                {/* <img
                                    src="/assets/grafana_creds_icon.svg" // New Icon
                                    alt="Get credentials icon"
                                    style={{ width: '24px', height: '24px', backgroundColor:'black' }}
                                /> */}
                                <Avatar sx={{ height: "2vw", width: "2vw",p:"0.75vw", marginRight: 0.5 , backgroundColor:"#333333"}} src="/assets/grafana_creds_icon.svg"/>
                                <Typography  sx={{ fontFamily: 'Gilroy-Medium', fontSize:'12px' }}>Get your login credentials for SonarQube dashboard</Typography>
                            </Box>
                            <Button
                                
                                size="small"
                                onClick={handleOpenModal}
                                sx={{ fontFamily: 'Gilroy-SemiBold', textTransform: 'none', whiteSpace: 'nowrap' , border:'none', color:'#101010', fontSize:'14px', borderRadius:'4'}}
                                disabled={isLoadingData || !fetchedUsername ||
        (user && user.roles && user.roles.includes("viewer"))} // Disable if loading page data or username not fetched
                            >
                                {/* {(isLoadingData && !fetchedUsername && !isModalOpen) ? <CircularProgress size={16} sx={{mr:1}} /> : null} */}
                                Get Credentials
                            </Button>
                        </Paper>
                    </Box>

                    {/* Bottom Section Button */}
                    <Button
                        variant="contained"
                        onClick={handleNavigateToDashboard}
                        size="large"
                        endIcon={<OpenInNewIcon />}
                        sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", py: 1.5, px: {xs: 3, md:5}, minWidth: { xs: '80%', md: '300px' }, '&:hover': { backgroundColor: "dimgray" }}}
                        disabled={isLoadingData || !fetchedDashboardUrl} // Disable if loading or URL not available
                    >
                        Take Me To SonarQube Dashboard
                    </Button>
                </Paper>
            </Container>

            {/* Credentials Modal Dialog */}
            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                PaperProps={{ sx: { borderRadius: '12px', width: '100%', maxWidth: '500px', p: 1 }}}
            >
                <DialogTitle sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.25rem', p: 2, pb: 1 }}>
                    SonarQube Login Credentials
                    <IconButton aria-label="close" onClick={handleCloseModal} sx={{ position: 'absolute', right: theme.spacing(2), top: theme.spacing(1.5), color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, pt: 1, minHeight: 150 }}>
                    {/* Modal content depends on overall page load error or specific credential data */}
                    {isLoadingData && (!fetchedUsername && !pageLoadError) ? ( // Show main loader if still loading data and no error yet
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2, fontFamily: 'Gilroy-Regular' }}>Loading credentials...</Typography>
                        </Box>
                    ) : pageLoadError ? ( // Show error if page load failed
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                            <ErrorOutlineIcon color="error" sx={{ fontSize: 40, mb: 1 }}/>
                            <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold', mb:1 }}>Failed to load data</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>{pageLoadError}</Typography>
                        </Box>
                    ) : fetchedUsername && fetchedPassword ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 2.5 }}>
                                Use the below username and password to login into the SonarQube dashboard.
                            </Typography>
                            <CredentialDisplayBox label="Username" value={fetchedUsername} />
                            <CredentialDisplayBox label="Password" value={fetchedPassword} />
                        </>
                    ) : ( // Fallback if not loading, no specific error, but credentials aren't available
                         <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                            <ErrorOutlineIcon color="disabled" sx={{ fontSize: 40, mb: 1 }}/>
                            <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-SemiBold', mb:1 }}>Credentials not available</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>Please try again or ensure the integration is correctly configured.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

const SonarQubeIntegrationPage: React.FC = () => {
    const theme = useTheme();
    // ... (MainDrawer logic remains the same)
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isYourAppDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState());

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isYourAppDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer // Replace with your actual MainDrawer component
                index={"integrations"}
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <SonarQubeIntegrationPageContent />
            </Box>
        </Box>
    );
};

export default SonarQubeIntegrationPage;